import {
  ContingencyPlanCreateRequest,
  ContingencyPlanDTO,
  ContingencyPlanUpdateRequest,
  ResidentBody
} from '@lmc/ts-common'
import { Form } from '@lmc/ui-kit'
import { showToast } from 'common/utils'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { handleOnFinishFailed } from '../../../utils/utils'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { FormInstance } from 'antd'
import { generalConstants } from 'locale/general'
import dayjs from 'dayjs'
import useContingencyPlan from 'hooks/useContingencyPlan'
import PlanNameFormCard from './FormCards/PlanName'
import OtherDetailsFormCard from './FormCards/OtherDetails'
import { ScenariosFormCard } from './FormCards/Scenarios'
import { AdvancedStatementsFormCard } from './FormCards/AdvancedStatements'

type ContingencyPlanFormProps = {
  create?: boolean
  form: FormInstance
  plan?: ContingencyPlanDTO | undefined
  setIsCreateLoading?: (value: boolean) => void
  setIsUpdateLoading?: (value: boolean) => void
  setIsDirty: (value: boolean) => void
}

type FormType = {
  nextReviewDate: dayjs.Dayjs
}

const ContingencyPlanForm = ({
  create,
  form,
  plan,
  setIsCreateLoading,
  setIsUpdateLoading,
  setIsDirty
}: ContingencyPlanFormProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const navigate = useNavigate()

  const { id: residentID, contingencyPlanID } = useParams()
  const { createContingencyPlan, updateContingencyPlan } = useContingencyPlan({
    contingencyPlanID,
    onCreateSuccess: () => {
      showToast({
        title: t(supConstants.FUTURE_PLAN_CREATED)
      })
      navigate(`../`, { relative: 'path' })
    },
    onUpdateSuccess: () => {
      showToast({
        title: t(supConstants.FUTURE_PLAN_UPDATED)
      })
      navigate(`../`, { relative: 'path' })
    }
  })

  const handleOnCreateFinish = async (
    values: Partial<ContingencyPlanCreateRequest>
  ) => {
    const convertedValues = {
      ...values,
      resident: residentID,
      scenarios: values.scenarios || [],
      agreedWithOtherPerson: values.agreedWithServiceUser
        ? ''
        : values.agreedWithOtherPerson
    }
    try {
      setIsCreateLoading?.(true)
      setIsDirty(false)
      await createContingencyPlan(
        convertedValues as ContingencyPlanCreateRequest
      )
    } catch {
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error'
      })
    }
    setIsCreateLoading?.(false)
  }

  const handleOnUpdateFinish = async (values: ContingencyPlanUpdateRequest) => {
    const convertedValues = {
      ...values,
      scenarios: values.scenarios || [],
      agreedWithOtherPerson: values.agreedWithServiceUser
        ? ''
        : values.agreedWithOtherPerson
    }
    try {
      setIsUpdateLoading?.(true)
      setIsDirty(false)
      await updateContingencyPlan(convertedValues)
    } catch {
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error'
      })
    }
    setIsUpdateLoading?.(false)
  }

  const handleFormValuesChange = (values: Partial<ResidentBody>) => {
    if (values) {
      setIsDirty(true)
    }
  }

  const initialValues: Partial<ContingencyPlanDTO> = {
    ...plan,
    nextReviewDate: undefined
  }

  return (
    <Form<FormType>
      form={form}
      initialValues={initialValues}
      onFinish={create ? handleOnCreateFinish : handleOnUpdateFinish}
      onValuesChange={handleFormValuesChange}
      onFinishFailed={({ errorFields }) => handleOnFinishFailed(errorFields)}
    >
      <PlanNameFormCard />
      <ScenariosFormCard />
      <AdvancedStatementsFormCard />
      <OtherDetailsFormCard plan={plan} />
    </Form>
  )
}

export default ContingencyPlanForm
